import React, { useEffect, useState, Component } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { supabase } from '../../lib/supabase';
import { useAdminOrders, useInventoryData, useDailySalesSummary } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime, getStatusColor, getStatusLabel } from '../../lib/utils';
import { ShoppingBag, Clock, CheckCircle2, AlertTriangle, Play, Pause, Eye, Utensils, Bike, ShoppingBasket, Send, Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { OrderDetailDrawer } from '../../components/admin/OrderDetailDrawer';
import { PayTableModal } from '../../components/admin/PayTableModal';
import { CabanaAddItemsModal } from '../../components/admin/CabanaAddItemsModal';
import { Receipt } from 'lucide-react';
import { buildWhatsAppUrl } from '../../lib/whatsapp';
export function DashboardPage() {
  const slug = useTenantSlug();
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    orders,
    loading: ordersLoading,
    updateOrderStatus,
    refetchOrders
  } = useAdminOrders(restaurant?.id);
  const {
    summary,
    loading: salesLoading
  } = useDailySalesSummary(restaurant?.id);
  const {
    alerts,
    loading: invLoading
  } = useInventoryData(restaurant?.id);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  // ─── Contador de envíos al mensajero (estado optimista local) ──────────
  // Se actualiza al instante cuando se presiona "Enviar a mensajero" para que
  // el dato sea visible de inmediato, sin depender de la latencia o de que la
  // escritura en la base se confirme. Se sincroniza con courier_notify_count
  // de la orden (la fuente compartida) tomando siempre el mayor de ambos.
  const [courierCounts, setCourierCounts] = useState<Record<string, number>>({});
  // ─── Órdenes con al menos un pago registrado ───────────────────────────
  // pos_quick_sale sin payments = intento abandonado, no pedido abierto real.
  // Cargamos el set de order_ids que SÍ tienen un payment para poder excluir
  // del listado de "pedidos abiertos" las ventas rápidas de Estantería que
  // quedaron huérfanas (creadas al abrir el modal de cobro y abandonadas sin
  // pagar). Las ventas rápidas que sí tienen pago NO se excluyen.
  const [paidOrderIds, setPaidOrderIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!restaurant?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('payments').select('order_id').eq('restaurant_id', restaurant.id);
        if (error) throw error;
        if (cancelled) return;
        setPaidOrderIds(new Set((data || []).map((p: any) => String(p.order_id))));
      } catch (e) {
        console.error('[NOIZA][Dashboard] Error loading paid order ids:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restaurant?.id, orders]);
  // ─── Cobro modal state ─────────────────────────────────────────────────
  // El botón "Cobrar y Facturar" abre PayTableModal con una "mesa" sintética
  // construida a partir de la orden. PayTableModal ya soporta órdenes sin
  // mesa real (delivery/pickup) usando orderId como fallback en
  // useTableOrderItems. Reutilizamos así TODA la funcionalidad de cobro
  // (split, parcial, factura "ahora / más tarde", auto-print a NOIZA_FACTURA).
  const [cobrarOrder, setCobrarOrder] = useState<any | null>(null);
  // NatuCafé: la adición conserva la orden abierta y solo incorpora
  // nuevos productos mediante el modal existente; no toca pagos previos.
  const [addItemsOrderId, setAddItemsOrderId] = useState<string | null>(null);
  useEffect(() => {
    if (settings) {
      console.log('[NOIZA][Dashboard] Settings loaded, kitchen_paused =', settings.kitchen_paused, 'restaurant_id =', restaurant?.id);
      setKitchenPaused(settings.kitchen_paused);
    }
  }, [settings, restaurant?.id]);
  const handleToggleKitchen = async () => {
    if (!restaurant) {
      console.error('[NOIZA][Dashboard] No restaurant loaded — cannot toggle');
      return;
    }
    setTogglingKitchen(true);
    setToggleError(null);
    const newValue = !kitchenPaused;
    console.log('[NOIZA][Dashboard] Toggling kitchen:', {
      restaurantId: restaurant.id,
      slug,
      currentValue: kitchenPaused,
      newValue
    });
    const {
      toggleKitchenPause
    } = await import('../../lib/kitchen-toggle');
    const result = await toggleKitchenPause(restaurant.id, newValue);
    if (result.success) {
      setKitchenPaused(result.newValue);
      setToggleError(null);
    } else {
      setKitchenPaused(result.newValue);
      setToggleError(result.error || 'No se pudo actualizar el estado de cocina');
      console.error('[NOIZA][Dashboard] Toggle FAILED:', result.error);
    }
    setTogglingKitchen(false);
  };
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-3600" data-mp="2blfehtgqhcr3">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-3601" data-mp="26supd58rvntw" />
      </div>;
  }
  const todayOrdersCount = summary?.orders_today || 0;
  const todayRevenue = summary?.total_today || 0;
  const newOrders = orders.filter(o => o.status === 'new');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');
  // ─── Open orders bucketed by delivery type ─────────────────────────────
  // Used for the new 3-card header (mesa / para llevar / delivery) and
  // the 3 collapsible "Pedidos Recientes" rows. Open = not delivered and
  // not cancelled. Same bucketing logic that Rancho uses below.
  const openOrders = orders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled' &&
  // pos_quick_sale sin payments = intento abandonado, no pedido abierto
  // real. Una venta rápida de Estantería se crea al abrir el modal de
  // cobro; si se abandona sin pagar queda pending/ready con
  // total_pagado=0. La excluimos para que no infle los contadores de
  // "pedidos de mesa". Las ventas rápidas que SÍ tienen pago se conservan.
  !((o as any).source === 'pos_quick_sale' && !paidOrderIds.has(String(o.id))));
  const openMesaOrders = openOrders.filter(o => o.delivery_type === 'dine_in' || o.delivery_type === 'mesa' || (o as any).table_number != null);
  const openPickupOrders = openOrders.filter(o => o.delivery_type === 'pickup');
  const openDeliveryOrders = openOrders.filter(o => o.delivery_type === 'delivery');
  // Heladería Congelados: flujo simplificado (Recibido / Completado)
  const isCongelados = String(restaurant?.id) === '18' || restaurant?.slug === 'heladeria-congelados';
  // NatuCafé: permite adicionar artículos desde este resumen sin salir
  // del pedido ni alterar los flujos compartidos de otros restaurantes.
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  // Rancho San Francisco (id 28) — vista de cuadros por tipo de orden.
  // Muestra los pedidos ACTIVOS (no entregados ni cancelados) agrupados
  // en tres buckets: Restaurante (mesa), Delivery y Para llevar.
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || restaurant?.slug === 'rancho-san-francisco';
  const ranchoActiveOrders = orders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled');
  const ranchoMesaOrders = ranchoActiveOrders.filter(o => o.delivery_type === 'dine_in' || o.delivery_type === 'mesa' || (o as any).table_number != null);
  const ranchoDeliveryOrders = ranchoActiveOrders.filter(o => o.delivery_type === 'delivery');
  const ranchoPickupOrders = ranchoActiveOrders.filter(o => o.delivery_type === 'pickup');
  // Derive alert counts from row-level v_inventory_alerts (real view uses uppercase values)
  const criticalAlerts = alerts.filter(a => a.alert_level === 'CRITICAL');
  const lowAlerts = alerts.filter(a => a.alert_level === 'LOW');
  // ─── Build a synthetic TableWithOrder for PayTableModal ───────────────
  // PayTableModal espera una "mesa" pero solo lee:
  //   table.id, table.table_number, table.current_order.* (id, order_number,
  //   created_at, subtotal, total, notes, payment_method).
  // Para órdenes de pickup/delivery no hay mesa real → construimos un
  // objeto mínimo. PayTableModal ya tiene fallback por orderId para
  // useTableOrderItems (línea 60-69 de PayTableModal), así que los items
  // se cargan correctamente vía orderId aunque table.id sea null.
  const buildSyntheticTable = (order: any): any => ({
    id: (order as any).table_id ?? null,
    restaurant_id: String(restaurant.id),
    table_number: (order as any).table_number ?? null,
    table_name: null,
    zone: null,
    capacity: null,
    qr_code_url: null,
    qr_token: null,
    is_active: true,
    created_at: order.created_at,
    updated_at: order.created_at,
    current_order: order,
    current_order_items: [],
    status: 'occupied',
    order_channel: null,
    time_open_minutes: null,
    item_count: 0
  });
  // ─── Enviar pedido a mensajero por WhatsApp + registrar el envío ─────────
  // Abre WhatsApp (de forma síncrona para no chocar con el bloqueo de
  // popups) y luego PERSISTE en la base de datos `courier_notified_at` /
  // `courier_notified_by`, de modo que TODOS los colaboradores vean si el
  // pedido ya fue enviado al mensajero (estado compartido, no local).
  const handleSendCourier = async (order: any) => {
    const url = buildCourierWhatsAppUrl(order, restaurant);
    // 1) Abrir WhatsApp inmediatamente dentro del gesto del usuario.
    const popup = window.open('', '_blank');
    if (popup) {
      popup.location.href = url;
    } else {
      window.open(url, '_blank');
    }
    // 2) Subir el contador LOCAL al instante para que el dato sea visible
    //    inmediatamente (no depende de la base ni del refetch).
    const dbCount = Number(order.courier_notify_count) || 0;
    const localPrev = courierCounts[String(order.id)] || 0;
    const newCount = Math.max(dbCount, localPrev) + 1;
    setCourierCounts(prev => ({
      ...prev,
      [String(order.id)]: newCount
    }));
    // 3) Persistir en la orden (fuente compartida entre colaboradores).
    //    - courier_notified_at / by: SIEMPRE el último envío.
    //    - courier_first_notified_at: solo la PRIMERA vez (inmutable), para
    //      medir el tiempo desde que entró el pedido hasta el 1er despacho.
    //    - courier_notify_count: cuántas veces se envió.
    try {
      const nowIso = new Date().toISOString();
      const update: Record<string, any> = {
        courier_notified_at: nowIso,
        courier_notified_by: tenantCtx?.userId || 'staff',
        courier_notify_count: newCount
      };
      if (!order.courier_first_notified_at) {
        update.courier_first_notified_at = nowIso;
      }
      const {
        error
      } = await supabase.from('orders').update(update).eq('id', order.id);
      if (error) throw error;
      if (refetchOrders) refetchOrders();
    } catch (e) {
      // Si la escritura falla (p. ej. faltan las columnas en la base), el
      // contador LOCAL ya quedó visible para esta sesión. Avisamos en consola.
      console.error('[NOIZA][Dashboard] No se pudo guardar el contador de envíos al mensajero. Verificá que se haya corrido ADD_NATUCAFE_COURIER_NOTIFIED.sql:', e);
    }
  };
  // Cantidad efectiva de envíos = el mayor entre el contador local (sesión)
  // y el guardado en la orden (compartido). Así el dato siempre se ve.
  const courierCountFor = (order: any): number => Math.max(courierCounts[String(order.id)] || 0, Number(order.courier_notify_count) || 0);
  const courierWasSent = (order: any): boolean => courierCountFor(order) > 0 || !!order.courier_notified_at;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3602" data-mp="m0ajvw5d1p0k">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-3603" data-mp="tjbchmemhy95">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-3604" data-mp="yiwfq90tssro">
          <div data-id="element-3605" data-mp="139crzpswu8cg">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3606" data-mp="139mmnqowh3oh">Dashboard</h1>
            <p className="text-gray-500" data-id="element-3607" data-mp="188pol29l2ts8">Resumen de operaciones de hoy</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-3608" data-mp="al8lx36fft98">
            <button onClick={handleToggleKitchen} disabled={togglingKitchen} className={`flex items-center gap-2 px-4 py-2 rounded-xl border shadow-sm transition-all active:scale-[0.98] ${togglingKitchen ? 'opacity-50 pointer-events-none' : ''} ${kitchenPaused ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-3609" data-mp="amij7q5ggxet">
              <span className="text-sm font-medium text-gray-600" data-id="element-3610" data-mp="1vyab1v4y4969">Cocina:</span>
              {kitchenPaused ? <Badge variant="danger" className="flex items-center gap-1" data-id="element-3611" data-mp="20zr6vc8kp5fz">
                  <Pause className="w-3 h-3" data-id="element-3612" data-mp="1yjhz0wsjzwgo" /> Pausada
                </Badge> : <Badge variant="success" className="flex items-center gap-1" data-id="element-3613" data-mp="1uiz7l3ukosf0">
                  <Play className="w-3 h-3" data-id="element-3614" data-mp="28i76rwqqk0j7" /> Activa
                </Badge>}
            </button>
          </div>
        </div>

        {/* Toggle Error */}
        {toggleError && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between" data-id="element-3615" data-mp="2hgspbrw7jfjz">
            <p className="text-red-700 text-sm font-medium" data-id="element-3616" data-mp="2dfqz20k9qdu5">⚠️ {toggleError}</p>
            <button onClick={() => setToggleError(null)} className="text-red-400 hover:text-red-600 text-xs font-bold ml-4" data-id="element-3617" data-mp="2r6ly6l4ytgkj">
              ✕
            </button>
          </div>}

        {/* Rancho San Francisco — 3-column bucket view (above stats) */}
        {isRanchoSanFrancisco && <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3618" data-mp="2w4ni18a7tjx2">
            {[{
          key: 'mesa',
          title: 'Restaurante',
          subtitle: 'Mesas activas',
          icon: Utensils,
          accent: 'bg-amber-50 border-amber-200 text-amber-900',
          badge: 'bg-amber-500 text-white',
          items: ranchoMesaOrders
        }, {
          key: 'delivery',
          title: 'Delivery',
          subtitle: 'Pedidos a domicilio',
          icon: Bike,
          accent: 'bg-blue-50 border-blue-200 text-blue-900',
          badge: 'bg-blue-500 text-white',
          items: ranchoDeliveryOrders
        }, {
          key: 'pickup',
          title: 'Para llevar',
          subtitle: 'Recoge en el local',
          icon: ShoppingBasket,
          accent: 'bg-emerald-50 border-emerald-200 text-emerald-900',
          badge: 'bg-emerald-500 text-white',
          items: ranchoPickupOrders
        }].map(bucket => {
          const Icon = bucket.icon;
          return <div key={bucket.key} className={`rounded-2xl border-2 ${bucket.accent} p-4 flex flex-col min-h-[320px]`} data-id="element-3619" data-mp="1pad2n1ammcsz">
                  <div className="flex items-center justify-between mb-4" data-id="element-3620" data-mp="1kd3dznv2a567">
                    <div className="flex items-center gap-3" data-id="element-3621" data-mp="2hxprxj868ypz">
                      <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center shadow-sm" data-id="element-3622" data-mp="2d07h1nalg9fc">
                        <Icon className="w-5 h-5" data-id="element-3623" data-mp="2801u2kf9rb3l" />
                      </div>
                      <div data-id="element-3624" data-mp="23209fv2733q0">
                        <h3 className="text-lg font-black leading-tight" data-id="element-3625" data-mp="230qcxaazq3lh">
                          {bucket.title}
                        </h3>
                        <p className="text-xs font-medium opacity-70" data-id="element-3626" data-mp="wjbjkp3tbx46">
                          {bucket.subtitle}
                        </p>
                      </div>
                    </div>
                    <span className={`min-w-[32px] h-8 px-2 rounded-full text-sm font-black flex items-center justify-center ${bucket.badge}`} data-id="element-3627" data-mp="337vh22pi9k4o">
                      {bucket.items.length}
                    </span>
                  </div>

                  {ordersLoading ? <div className="space-y-2" data-id="element-3628" data-mp="3my7hkcw2k5xp">
                      <Skeleton className="h-16 w-full" data-id="element-3629" data-mp="3mxe6x0tzowaa" />
                      <Skeleton className="h-16 w-full" data-id="element-3630" data-mp="3qz9a6avhdjqp" />
                    </div> : bucket.items.length === 0 ? <div className="flex-1 flex items-center justify-center text-sm font-medium opacity-50 italic" data-id="element-3631" data-mp="3vzx69aykgqrs">
                      Sin pedidos activos
                    </div> : <div className="space-y-2 overflow-y-auto max-h-[480px] pr-1" data-id="element-3632" data-mp="21m5r5vxgxeoy">
                      {bucket.items.map(order => <button key={order.id} type="button" onClick={() => setSelectedOrderId(String(order.id))} className="w-full bg-white rounded-xl p-3 border border-white/50 shadow-sm hover:shadow-md transition-all text-left" data-id="element-3633" data-mp="cbtxomyd87zv">
                          <div className="flex justify-between items-start mb-1.5" data-id="element-3634" data-mp="h328nnfcg1f5">
                            <span className="font-black text-gray-900 text-sm" data-id="element-3635" data-mp="3pj54mvx3g1nn">
                              #{order.order_number}
                            </span>
                            <span className="text-sm font-black tabular-nums text-gray-900" data-id="element-3636" data-mp="3boqodx33bm8">
                              {formatCurrency(order.total)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs" data-id="element-3637" data-mp="1ep9cgv3tppqg">
                            <span className="font-medium text-gray-700 truncate max-w-[60%]" data-id="element-3638" data-mp="19rr4p81jlgk1">
                              {(order as any).table_number ? `Mesa ${(order as any).table_number}` : order.customer_name || 'Sin nombre'}
                            </span>
                            <span className="text-gray-500 font-medium" data-id="element-3639" data-mp="18bmzxvazc2i1">
                              {formatRelativeTime(order.created_at)}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center justify-between" data-id="element-3640" data-mp="202066bcub56l">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(order.status)}`} data-id="element-3641" data-mp="24z023tfz6rjz">
                              {getStatusLabel(order.status)}
                            </span>
                            <span className="text-[10px] font-bold text-gray-400 flex items-center gap-1" data-id="element-3642" data-mp="2iyj9gk0ypyv8">
                              <Eye className="w-3 h-3" data-id="element-3643" data-mp="2ns3wjh10p8fn" />
                              Ver detalle
                            </span>
                          </div>
                        </button>)}
                    </div>}
                </div>;
        })}
          </div>}

        {/* Stats Grid — 3 cards by order type (open orders only) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3644" data-mp="edpvp3l99z45">
          <Card data-id="element-3645" data-mp="f00xulwsbiy">
            <CardContent className="p-6" data-id="element-3646" data-mp="eghg9gm1f2t">
              <div className="flex items-center justify-between mb-4" data-id="element-3647" data-mp="5eup3qeaun0b">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-3648" data-mp="5d1ho8vv3yr9">
                  <Utensils className="w-5 h-5 text-amber-600" data-id="element-3649" data-mp="3mpccfztuto8a" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-3650" data-mp="3rmkve2mttwal">
                  {openMesaOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-3651" data-mp="1pvhhn4hn41lt">
                Pedidos de mesa
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-3652" data-mp="3ae1rynzx24us">Abiertos ahora</p>
            </CardContent>
          </Card>

          <Card data-id="element-3653" data-mp="35f8gf146wnx9">
            <CardContent className="p-6" data-id="element-3654" data-mp="35fqhcb1srqbi">
              <div className="flex items-center justify-between mb-4" data-id="element-3655" data-mp="3k3bk23urfniw">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-3656" data-mp="3k4mpe83ut3c4">
                  <ShoppingBasket className="w-5 h-5 text-emerald-600" data-id="element-3657" data-mp="3faa55vujznzb" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-3658" data-mp="3t5cg4xe9c3iz">
                  {openPickupOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-3659" data-mp="6v8escb1vi15">
                Pedidos para llevar
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-3660" data-mp="1xq5ulx192t5">Abiertos ahora</p>
            </CardContent>
          </Card>

          <Card data-id="element-3661" data-mp="28pr07s7kzjhb">
            <CardContent className="p-6" data-id="element-3662" data-mp="23ojrnqxm6g9l">
              <div className="flex items-center justify-between mb-4" data-id="element-3663" data-mp="23pleesnzsr75">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-3664" data-mp="7lyjbvbw4uf6">
                  <Bike className="w-5 h-5 text-blue-600" data-id="element-3665" data-mp="ck018cxkynp2" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-3666" data-mp="gm3en73t82cz">
                  {openDeliveryOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-3667" data-mp="lmrgkntku3lz">
                Pedidos delivery
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-3668" data-mp="qk14fz1bi77p">Abiertos ahora</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3669" data-mp="10a2omfqjv5hc">
          {/* Recent Orders — 3 collapsible dropdowns by type */}
          <Card className="lg:col-span-2" data-id="element-3670" data-mp="14bxttvblq30e">
            <CardHeader data-id="element-3671" data-mp="14dsc11vhnh0s">
              <CardTitle data-id="element-3672" data-mp="19b0xbbbxo169">Pedidos Recientes</CardTitle>
              <p className="text-xs text-gray-500 mt-1" data-id="element-3673" data-mp="19cc2nfl11gzh">
                Pedidos abiertos agrupados por tipo. Tocá una fila para
                expandir.
              </p>
            </CardHeader>
            <CardContent data-id="element-3674" data-mp="1ofdh63y419pm">
              {ordersLoading ? <div className="space-y-4" data-id="element-3675" data-mp="1je67u0gbk2gu">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-3676" data-mp="1efcr7z5qjvcg" />)}
                </div> : <div className="space-y-3" data-id="element-3677" data-mp="1af2piyoz7r3e">
                  <OrderTypeDropdown title="Delivery" subtitle="Pedidos a domicilio" icon={Bike} accent="bg-blue-50 border-blue-200 text-blue-900" badge="bg-blue-500 text-white" orders={openDeliveryOrders} isCongelados={isCongelados} restaurant={restaurant} onSelectOrder={id => setSelectedOrderId(id)} onUpdateStatus={updateOrderStatus} onCobrar={order => setCobrarOrder(order)} canAddProducts={isNatuCafe} onAddProducts={order => setAddItemsOrderId(String(order.id))} onSendCourier={handleSendCourier} courierCountFor={courierCountFor} courierWasSent={courierWasSent} data-id="element-3678" data-mp="1ag3xguap3xgw" />
                  <OrderTypeDropdown title="Mesa" subtitle="Pedidos en el local" icon={Utensils} accent="bg-amber-50 border-amber-200 text-amber-900" badge="bg-amber-500 text-white" orders={openMesaOrders} isCongelados={isCongelados} restaurant={restaurant} onSelectOrder={id => setSelectedOrderId(id)} onUpdateStatus={updateOrderStatus} onCobrar={order => setCobrarOrder(order)} canAddProducts={isNatuCafe} onAddProducts={order => setAddItemsOrderId(String(order.id))} onSendCourier={handleSendCourier} courierCountFor={courierCountFor} courierWasSent={courierWasSent} data-id="element-3679" data-mp="20o07bktpyh6v" />
                  <OrderTypeDropdown title="Para llevar" subtitle="Recoge en el local" icon={ShoppingBasket} accent="bg-emerald-50 border-emerald-200 text-emerald-900" badge="bg-emerald-500 text-white" orders={openPickupOrders} isCongelados={isCongelados} restaurant={restaurant} onSelectOrder={id => setSelectedOrderId(id)} onUpdateStatus={updateOrderStatus} onCobrar={order => setCobrarOrder(order)} canAddProducts={isNatuCafe} onAddProducts={order => setAddItemsOrderId(String(order.id))} onSendCourier={handleSendCourier} courierCountFor={courierCountFor} courierWasSent={courierWasSent} data-id="element-3680" data-mp="15dva15jvo7fu" />
                  {openOrders.length === 0 && <div className="text-center py-8 text-gray-500 text-sm" data-id="element-3681" data-mp="2k1ydcsjl9sep">
                      No hay pedidos abiertos
                    </div>}
                </div>}
            </CardContent>
          </Card>

          {/* Alerts & Info */}
          <div className="space-y-6" data-id="element-3682" data-mp="1wdot3v20xcpo">
            <Card data-id="element-3683" data-mp="1wcnb16mlbpyg">
              <CardHeader data-id="element-3684" data-mp="1wbc4x05oa646">
                <CardTitle className="flex items-center gap-2" data-id="element-3685" data-mp="2145cdvsizsjy">
                  <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-3686" data-mp="215gezsa8iu9h" />
                  Alertas de Inventario
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-3687" data-mp="18n1dsops3kt3">
                {invLoading ? <Skeleton className="h-20 w-full" data-id="element-3688" data-mp="3ly42atkgol9o" /> : alerts.length > 0 ? <div className="space-y-4" data-id="element-3689" data-mp="3hwiv9oulz17e">
                    {criticalAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-3690" data-mp="3cxfpg26t0qyd">
                        <span className="text-sm font-medium text-red-800" data-id="element-3691" data-mp="37yup82rjy79x">
                          Stock Crítico
                        </span>
                        <Badge variant="danger" data-id="element-3692" data-mp="330jd1ldsbkoj">{criticalAlerts.length}</Badge>
                      </div>}
                    {lowAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-3693" data-mp="2u7pvfhav897i">
                        <span className="text-sm font-medium text-amber-800" data-id="element-3694" data-mp="2p9obyv9j27vi">
                          Stock Bajo
                        </span>
                        <Badge variant="warning" data-id="element-3695" data-mp="2f95m9jva7x5o">{lowAlerts.length}</Badge>
                      </div>}
                    <div className="space-y-2 mt-2" data-id="element-3696" data-mp="7zia7rxp9ipy">
                      {alerts.slice(0, 5).map(alert => <div key={alert.inventory_item_id} className="flex justify-between text-sm" data-id="element-3697" data-mp="bzis5mk0cc1r">
                          <span className="text-gray-700 truncate" data-id="element-3698" data-mp="2yb6q52lb5p3">
                            {alert.ingredient_name}
                          </span>
                          <span className="text-gray-500 shrink-0 ml-2" data-id="element-3699" data-mp="3ke17888d8ruv">
                            {alert.current_stock} / {alert.minimum_stock}{' '}
                            {alert.unit}
                          </span>
                        </div>)}
                    </div>
                  </div> : <p className="text-sm text-gray-500" data-id="element-3700" data-mp="31i1c4ok3d872">
                    No hay alertas de inventario.
                  </p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <OrderDetailDrawer isOpen={!!selectedOrderId} onClose={() => setSelectedOrderId(null)} orderId={selectedOrderId} data-id="element-3701" data-mp="2mukofwyknzkf" />

      {/* ─── Cobrar y Facturar — Modal de cobro reutilizando PayTableModal ── */}
      <PayTableModal isOpen={!!cobrarOrder} onClose={() => setCobrarOrder(null)} table={cobrarOrder ? buildSyntheticTable(cobrarOrder) : null} restaurantId={restaurant?.id} onSuccess={() => {
      // Refrescamos la lista de órdenes cuando se completa un cobro
      // (parcial o total). PayTableModal se cierra solo a través del
      // botón de cerrar; nosotros nos limitamos a refrescar el state.
      if (refetchOrders) refetchOrders();
    }} createdBy={tenantCtx?.userId || null} data-id="element-3702" data-mp="2rtnjcoku8peo" />

      {isNatuCafe && <CabanaAddItemsModal isOpen={!!addItemsOrderId} onClose={() => setAddItemsOrderId(null)} orderId={addItemsOrderId} restaurantId={restaurant.id} onAdded={() => {
      setAddItemsOrderId(null);
      refetchOrders();
    }} data-id="element-3703" data-mp="3ki1mtjup4dxm" />}
    </AdminLayout>;
}
// ─── Collapsible dropdown for a single order-type bucket ─────────────────
interface OrderTypeDropdownProps {
  title: string;
  subtitle: string;
  icon: ComponentType<any>;
  accent: string;
  badge: string;
  orders: any[];
  isCongelados: boolean;
  restaurant?: any;
  onSelectOrder: (id: string) => void;
  onUpdateStatus: (id: string, status: string) => void;
  onCobrar: (order: any) => void;
  canAddProducts?: boolean;
  onAddProducts?: (order: any) => void;
  onSendCourier?: (order: any) => void;
  courierCountFor?: (order: any) => number;
  courierWasSent?: (order: any) => boolean;
}
// ─── WhatsApp para mensajero (solo pedidos delivery) ─────────────────────
// Determina si un pedido delivery tiene datos completos para despachar al
// mensajero: nombre + teléfono del cliente y ALGUNA referencia de ubicación
// (dirección de texto, maps_url o lat/lng).
function deliveryHasCompleteData(order: any): boolean {
  const hasContact = !!order?.customer_name && !!order?.customer_phone;
  const hasLocation = !!order?.delivery_address || !!order?.maps_url || order?.delivery_lat != null && order?.delivery_lng != null;
  return hasContact && hasLocation;
}
// Tiempo desde que ENTRÓ el pedido (created_at) hasta el PRIMER envío al
// mensajero (courier_first_notified_at). Base para medir preparación.
// Devuelve un prefijo legible (ej. "Prep 18 min · ") o '' si no aplica.
function courierPrepLabel(order: any): string {
  const start = order?.created_at;
  const first = order?.courier_first_notified_at || order?.courier_notified_at;
  if (!start || !first) return '';
  const ms = new Date(first).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `Prep ${mins} min · `;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `Prep ${h}h ${m}m · `;
}
// Construye el mensaje de WhatsApp listo para enviar al mensajero y devuelve
// el wa.me URL (sin número fijo: el usuario elige el contacto del mensajero).
function buildCourierWhatsAppUrl(order: any, restaurant: any): string {
  const lat = order?.delivery_lat;
  const lng = order?.delivery_lng;
  const hasCoords = lat != null && lng != null;
  const googleMaps = order?.maps_url || (hasCoords ? `https://www.google.com/maps?q=${lat},${lng}` : '—');
  const waze = hasCoords ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` : '—';
  // MS Express (entrega externa): cuando la orden trae zona/localidad del
  // catálogo, mostramos la zona + localidad + costo del delivery, y la
  // dirección escrita por el cliente pasa a ser "Notas de la Dirección de
  // entrega". Para órdenes sin zona (otros restaurantes / flujo viejo) se
  // mantiene EXACTAMENTE la línea original — no se rompe nada.
  const hasMsExpress = !!order?.delivery_zone || !!order?.delivery_subzone;
  const deliveryFeeNum = Number(order?.delivery_fee) || 0;
  const deliveryLines = hasMsExpress ? [`Dirección de entrega: ${order?.delivery_zone || '—'}, ${order?.delivery_subzone || '—'}`, `Costo del delivery: ₡${deliveryFeeNum.toLocaleString('es-CR')}`, `*Notas de la Dirección de entrega:* ${order?.delivery_address || '—'}`] : [`*Dirección de entrega:* ${order?.delivery_address || '—'}`];
  const lines = ['🛵 *Nuevo pedido para entrega*', '', `*Restaurante:* ${restaurant?.name || '—'}`, `*Dirección restaurante:* ${restaurant?.address_text || '—'}`, `*Cliente:* ${order?.customer_name || '—'}`, `*Teléfono:* ${order?.customer_phone || '—'}`, ...deliveryLines, '', '📍 *Ubicación:*', `Google Maps: ${googleMaps}`, `Waze: ${waze}`, '', `*Pedido:* ${order?.order_number || '—'}`];
  // Número PROVISIONAL del mensajero. Cambiar/parametrizar a futuro.
  // NatuCafé Coffee Shop (restaurant_id 19) usa su propio mensajero.
  const isNatuCafe = String(restaurant?.id) === '19';
  const COURIER_PHONE = isNatuCafe ? '+50688518989' : '+50685316690';
  return buildWhatsAppUrl(COURIER_PHONE, lines.join('\n'));
}
function OrderTypeDropdown({
  title,
  subtitle,
  icon: Icon,
  accent,
  badge,
  orders,
  isCongelados,
  restaurant,
  onSelectOrder,
  onUpdateStatus,
  onCobrar,
  canAddProducts,
  onAddProducts,
  onSendCourier,
  courierCountFor,
  courierWasSent
}: OrderTypeDropdownProps) {
  const [open, setOpen] = useState(orders.length > 0);
  // Auto-open if there are orders, but respect manual collapse.
  // We initialize once based on count; the user can toggle freely after.
  return <div className={`rounded-2xl border-2 ${accent} overflow-hidden`} data-id="element-3704" data-mp="2sc71ch4dxkou">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between p-4 hover:bg-white/30 transition-colors text-left" data-id="element-3705" data-mp="3pv06ornbnjxz">
        <div className="flex items-center gap-3" data-id="element-3706" data-mp="3tcxnc6hnqham">
          <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center shadow-sm" data-id="element-3707" data-mp="170pts7e4nkp">
            <Icon className="w-5 h-5" data-id="element-3708" data-mp="663vnev72xtq" />
          </div>
          <div data-id="element-3709" data-mp="68q2fca9h9aw">
            <h3 className="text-base font-black leading-tight" data-id="element-3710" data-mp="b5q9nqiuki3l">{title}</h3>
            <p className="text-xs font-medium opacity-70" data-id="element-3711" data-mp="b71dtrg7frv8">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3712" data-mp="g4ks2rcwk4n6">
          <span className={`min-w-[32px] h-7 px-2 rounded-full text-sm font-black flex items-center justify-center ${badge}`} data-id="element-3713" data-mp="k5wlz92t7c16">
            {orders.length}
          </span>
          <ChevronIcon open={open} data-id="element-3714" data-mp="twr4c2cxprhc" />
        </div>
      </button>

      {open && <div className="border-t border-white/40 bg-white/40 p-3 space-y-2" data-id="element-3715" data-mp="yvklc4rfk0m9">
          {orders.length === 0 ? <div className="py-6 text-center text-sm font-medium opacity-60 italic" data-id="element-3716" data-mp="13tvvyhpjuf5j">
              Sin pedidos abiertos
            </div> : orders.map(order => <div key={order.id} className="bg-white rounded-xl p-3 border border-white/60 shadow-sm" data-id="element-3717" data-mp="97f2axa3223q">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2" data-id="element-3718" data-mp="nxe02yxq7bio">
                  <div className="flex flex-col" data-id="element-3719" data-mp="j3ryq2x0iu26">
                    <span className="font-black text-gray-900 text-sm" data-id="element-3720" data-mp="wwhmabs6w71e">
                      #{order.order_number}
                    </span>
                    <span className="text-xs text-gray-500" data-id="element-3721" data-mp="rwvam6s0eyup">
                      {formatRelativeTime(order.created_at)}
                    </span>
                  </div>
                  <div className="text-right" data-id="element-3722" data-mp="11vkqxv49cb8u">
                    <div className="text-sm font-black tabular-nums text-gray-900" data-id="element-3723" data-mp="11triqbdzxiyq">
                      {formatCurrency(order.total)}
                    </div>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(order.status)}`} data-id="element-3724" data-mp="33o8z8ivrdovh">
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-gray-700 mb-2 truncate" data-id="element-3725" data-mp="19f3hcmftz6rg">
                  {(order as any).table_number ? `Mesa ${(order as any).table_number}` : order.customer_name || 'Sin nombre'}
                </div>

                <div className="flex items-center gap-2 flex-wrap" data-id="element-3726" data-mp="14giq3cik3xf5">
                  <Button size="sm" onClick={() => onSelectOrder(String(order.id))} data-id="element-3727" data-mp="qghg97kbdfnj">
                    <Eye className="w-4 h-4 mr-1" data-id="element-3728" data-mp="vgdjh4l6t321" />
                    Ver detalle
                  </Button>
                  {isCongelados ? <>
                      {(order.status === 'new' || order.status === 'accepted') && <Button size="sm" variant="secondary" onClick={() => onUpdateStatus(order.id, 'preparing')} data-id="element-3729" data-mp="1cn0fo2sl5rtx">
                          ✅ Recibido
                        </Button>}
                      {(order.status === 'preparing' || order.status === 'ready') && <Button size="sm" variant="secondary" onClick={() => onUpdateStatus(order.id, 'delivered')} data-id="element-3730" data-mp="txu4d0u4g2i5">
                          🎉 Completado
                        </Button>}
                    </> : <>
                      {order.status === 'accepted' && <Button size="sm" variant="secondary" onClick={() => onUpdateStatus(order.id, 'preparing')} data-id="element-3731" data-mp="2aaiu9u8xho5u">
                          Preparar
                        </Button>}
                    </>}
                </div>

                {canAddProducts && !['cancelled', 'delivered', 'out_for_delivery'].includes(order.status) && <button type="button" onClick={() => onAddProducts?.(order)} className="mt-3 w-full flex items-center gap-3 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] transition-all text-white font-bold rounded-xl p-3 shadow-sm" data-id="element-3732" data-mp="1ywh5y7bgb1hk">
                      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0" data-id="element-3733" data-mp="2csm1kz1vwn8p">
                        <Plus className="w-5 h-5" data-id="element-3734" data-mp="3o9u4th0snxba" />
                      </div>
                      <div className="text-left flex-1 min-w-0" data-id="element-3735" data-mp="3obyiurseqcpa">
                        <div className="text-sm font-black leading-tight" data-id="element-3736" data-mp="3k7k9tw3bw4nc">
                          Agregar productos
                        </div>
                        <div className="text-[11px] font-medium opacity-90" data-id="element-3737" data-mp="3u4gchl6jpwjz">
                          Añadir artículos a este pedido abierto
                        </div>
                      </div>
                    </button>}

                {/* ─── Cobrar y Facturar — abre PayTableModal con el flow completo ─ */}
                <button type="button" onClick={() => onCobrar(order)} className="mt-3 w-full flex items-center gap-3 bg-orange-500 hover:bg-orange-600 active:scale-[0.99] transition-all text-white font-bold rounded-xl p-3 shadow-sm" data-id="element-3738" data-mp="bxs7rwferbeu">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0" data-id="element-3739" data-mp="prjih84vl1c5">
                    <Receipt className="w-5 h-5" data-id="element-3740" data-mp="upur5fjfp5ss" />
                  </div>
                  <div className="text-left flex-1 min-w-0" data-id="element-3741" data-mp="2f7m0yftchfs2">
                    <div className="text-sm font-black leading-tight" data-id="element-3742" data-mp="2a896b8w8f4ex">
                      Cobrar y Facturar
                    </div>
                    <div className="text-[11px] font-medium opacity-90" data-id="element-3743" data-mp="265w4gvklvqnf">
                      Recibir el pago y emitir la factura
                    </div>
                  </div>
                </button>

                {/* ─── Enviar pedido a mensajero — solo pedidos delivery ─── */}
                {order.delivery_type === 'delivery' && (deliveryHasCompleteData(order) ? (courierWasSent ? courierWasSent(order) : !!order.courier_notified_at) ? <button type="button" onClick={() => onSendCourier?.(order)} className="mt-2 w-full flex items-center gap-3 bg-emerald-50 hover:bg-emerald-100 active:scale-[0.99] transition-all text-emerald-800 font-bold rounded-xl p-3 border-2 border-emerald-300" data-id="element-3744" data-mp="3hmchki8otv74">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0" data-id="element-3745" data-mp="4mwetygwz9ac">
                          <CheckCircle2 className="w-5 h-5 text-white" data-id="element-3746" data-mp="3r04mbmh5d0lh" />
                        </div>
                        <div className="text-left flex-1 min-w-0" data-id="element-3747" data-mp="3d0c0o5kh9akd">
                          <div className="text-sm font-black leading-tight" data-id="element-3748" data-mp="3d37yltwbrz8t">
                            Enviado a mensajero
                            {(courierCountFor ? courierCountFor(order) : Number(order.courier_notify_count) || 0) >= 1 ? ` (×${courierCountFor ? courierCountFor(order) : Number(order.courier_notify_count) || 0})` : ''}
                          </div>
                          <div className="text-[11px] font-medium opacity-90" data-id="element-3749" data-mp="168b3krijofsi">
                            {courierPrepLabel(order)}
                            {formatRelativeTime(order.courier_notified_at)}
                            {order.courier_notified_by ? ` · ${String(order.courier_notified_by).slice(0, 8)}` : ''}{' '}
                            · Tocá para reenviar
                          </div>
                        </div>
                      </button> : <button type="button" onClick={() => onSendCourier?.(order)} className="mt-2 w-full flex items-center gap-3 bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition-all text-white font-bold rounded-xl p-3 shadow-sm" data-id="element-3750" data-mp="35gavp1idje51">
                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0" data-id="element-3751" data-mp="1p56w1liump9e">
                          <Send className="w-5 h-5" data-id="element-3752" data-mp="1mioax2wawsgu" />
                        </div>
                        <div className="text-left flex-1 min-w-0" data-id="element-3753" data-mp="1ml0qxaii0sr9">
                          <div className="text-sm font-black leading-tight" data-id="element-3754" data-mp="1qiyk0ofe3yzx">
                            Enviar pedido a mensajero
                          </div>
                          <div className="text-[11px] font-medium opacity-90" data-id="element-3755" data-mp="20iz44cl32j56">
                            Abre WhatsApp con los datos de entrega
                          </div>
                        </div>
                      </button> : <button type="button" disabled className="mt-2 w-full flex items-center gap-3 bg-gray-100 text-gray-400 cursor-not-allowed font-bold rounded-xl p-3" data-id="element-3756" data-mp="1csv2garggtgt">
                      <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center shrink-0" data-id="element-3757" data-mp="k3o1l54j3rse">
                        <Send className="w-5 h-5" data-id="element-3758" data-mp="p17fu51884kc" />
                      </div>
                      <div className="text-left flex-1 min-w-0" data-id="element-3759" data-mp="hj99g9ecd9lc">
                        <div className="text-sm font-black leading-tight" data-id="element-3760" data-mp="ck4ypebrr3sw">
                          Faltan datos de entrega
                        </div>
                        <div className="text-[11px] font-medium opacity-90" data-id="element-3761" data-mp="7nocwqs1l1iz">
                          Sin dirección ni ubicación para el mensajero
                        </div>
                      </div>
                    </button>)}
              </div>)}
        </div>}
    </div>;
}
function ChevronIcon({
  open
}: {
  open: boolean;
}) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-3762" data-mp="3l0rgiemaickp">
      <polyline points="6 9 12 15 18 9" data-id="element-3763" data-mp="37205j12q570s" />
    </svg>;
}
// Helper icon
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3764" data-mp="2wea9sgigwkk9">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-3765" data-mp="2sd88lu4zpyfn" />
      <path d="M6 17h12" data-id="element-3766" data-mp="36e14ojaxx1kv" />
    </svg>;
}